/**
 * CSTicketsBoardPanel — Section 3 (iter-140) · Customer Service Tickets Board.
 *
 * Consumes the TKT- lifecycle (Open → Assigned → Resolved → Closed)
 * built in the platform-numbering system.
 *
 * Row shows: TKT- number · property · NECS- · issue type · reporter
 * type · age · assignee · status. Clicking a row expands the detail
 * inline with lifecycle action buttons. Resolve requires a resolution
 * note before the Close button unlocks.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw, TicketCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';

const STATUS_COLORS = {
  open: 'bg-red-600 text-white',
  assigned: 'bg-amber-500 text-black',
  resolved: 'bg-blue-600 text-white',
  closed: 'bg-emerald-700 text-white',
};

const REPORTER_LABELS = {
  tenant: 'Resident',
  resident: 'Resident',
  agent: 'Field Agent',
  supervisor: 'Supervisor',
  crew_leader: 'Crew Leader',
  property_manager: 'Property Manager',
  admin: 'NECS Admin',
  company_manager: 'NECS Admin',
};

function ageOf(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const hours = Math.max(0, (now - then) / 3_600_000);
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

export default function CSTicketsBoardPanel({ testId = 'cs-tickets-board' }) {
  const [tickets, setTickets] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [issueFilter, setIssueFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [openId, setOpenId] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const url = statusFilter && statusFilter !== 'all'
      ? `${API}/api/tickets?status=${statusFilter}`
      : `${API}/api/tickets`;
    try {
      const r = await fetch(url, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setTickets(d.tickets || []);
      } else setTickets([]);
    } catch { setTickets([]); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!tickets) return [];
    return tickets.filter((t) => {
      if (propertyFilter && !(t.property_name || '').toLowerCase().includes(propertyFilter.toLowerCase())) return false;
      if (issueFilter && !(t.reason || '').toLowerCase().includes(issueFilter.toLowerCase())) return false;
      if (assigneeFilter && (t.assignee_id || '').toLowerCase() !== assigneeFilter.toLowerCase()) return false;
      return true;
    });
  }, [tickets, propertyFilter, issueFilter, assigneeFilter]);

  const openCount = useMemo(
    () => (tickets || []).filter((t) => t.status === 'open' || t.status === 'assigned').length,
    [tickets],
  );

  // Iter-141 · Section 3 — KPI strip. Recomputes whenever the tickets
  // list refreshes so the numbers always match what's on the board.
  const kpis = useMemo(() => {
    const rows = tickets || [];
    const openOrAssigned = rows.filter((t) => t.status === 'open' || t.status === 'assigned');
    // Avg Age — hours across every currently-open ticket.
    let avgAgeHours = 0;
    if (openOrAssigned.length) {
      const now = Date.now();
      const total = openOrAssigned.reduce((acc, t) => {
        const then = t.created_at ? new Date(t.created_at).getTime() : now;
        return acc + Math.max(0, (now - then) / 3_600_000);
      }, 0);
      avgAgeHours = total / openOrAssigned.length;
    }
    // Same-Day Resolved — tickets created and closed within the same
    // calendar day (best-effort using created_at + lifecycle "closed" step).
    const sameDayResolved = rows.reduce((acc, t) => {
      const created = t.created_at ? new Date(t.created_at) : null;
      if (!created) return acc;
      const closedStep = (t.lifecycle || []).find((s) => s.status === 'closed');
      const closedAt = closedStep?.at
        ? new Date(closedStep.at)
        : (t.status === 'closed' && t.updated_at ? new Date(t.updated_at) : null);
      if (!closedAt) return acc;
      if (
        created.getFullYear() === closedAt.getFullYear() &&
        created.getMonth() === closedAt.getMonth() &&
        created.getDate() === closedAt.getDate()
      ) return acc + 1;
      return acc;
    }, 0);
    return {
      open: openOrAssigned.length,
      avgAgeHours: avgAgeHours ? avgAgeHours.toFixed(1) : '0.0',
      sameDayResolved,
    };
  }, [tickets]);

  const transition = async (tk, status) => {
    if (status === 'closed' && (tk.status !== 'resolved')) {
      toast.error('Only Resolved tickets can be Closed');
      return;
    }
    if (status === 'resolved' && !resolutionNote.trim()) {
      toast.error('Add a resolution note first');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/tickets/${tk.ticket_number}/transition`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          resolution_note: status === 'resolved' ? resolutionNote.trim() : undefined,
        }),
      });
      if (r.ok) {
        toast.success(`Ticket ${tk.ticket_number} → ${status.replace('_', ' ')}`);
        setResolutionNote('');
        load();
      } else {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || 'Transition failed');
      }
    } finally { setBusy(false); }
  };

  return (
    <section className="rounded-xl border border-zinc-700 bg-zinc-900/70 p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em]" style={{ color: GOLD }}>
          <TicketCheck className="h-4 w-4" /> Customer Service Tickets
          <Badge className="ml-2 bg-red-600 text-white" data-testid={`${testId}-open-count`}>
            {openCount} open
          </Badge>
        </h2>
        <button onClick={load} className="p-1.5 rounded-full border border-zinc-600 text-zinc-300 hover:text-white" data-testid={`${testId}-refresh`}>
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Iter-141 · Section 3 — compact KPI strip above the board. */}
      <div className="grid grid-cols-3 gap-2 mb-3" data-testid={`${testId}-kpis`}>
        <KpiTile testId={`${testId}-kpi-open`} label="Open" value={kpis.open} accent="text-red-400" />
        <KpiTile testId={`${testId}-kpi-avg-age`} label="Avg Age" value={`${kpis.avgAgeHours}h`} accent="text-amber-300" />
        <KpiTile testId={`${testId}-kpi-same-day`} label="Same-Day Resolved" value={kpis.sameDayResolved} accent="text-emerald-400" />
      </div>

      <div className="grid md:grid-cols-4 gap-2 mb-3" data-testid={`${testId}-filters`}>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="h-9 px-2 rounded bg-zinc-950 border border-zinc-700 text-white text-xs"
                data-testid={`${testId}-filter-status`}>
          <option value="all">All statuses</option>
          <option value="open">Open</option>
          <option value="assigned">Assigned</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <Input placeholder="Property…" value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)}
               className="bg-zinc-950 border-zinc-700 text-white text-xs h-9" data-testid={`${testId}-filter-property`} />
        <Input placeholder="Issue type…" value={issueFilter} onChange={(e) => setIssueFilter(e.target.value)}
               className="bg-zinc-950 border-zinc-700 text-white text-xs h-9" data-testid={`${testId}-filter-issue`} />
        <Input placeholder="Assigned CS agent id…" value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}
               className="bg-zinc-950 border-zinc-700 text-white text-xs h-9" data-testid={`${testId}-filter-assignee`} />
      </div>

      {tickets === null ? (
        <p className="text-xs text-zinc-500" data-testid={`${testId}-loading`}>Loading tickets…</p>
      ) : filtered.length === 0 ? (
        <p className="text-xs text-zinc-500 py-6 text-center" data-testid={`${testId}-empty`}>
          No tickets match these filters.
        </p>
      ) : (
        <div className="divide-y divide-zinc-800 rounded-lg border border-zinc-800 overflow-hidden">
          {filtered.map((t) => {
            const isOpen = openId === t.ticket_number;
            const reporterLabel = REPORTER_LABELS[t.reporter_role] || t.reporter_role || '—';
            return (
              <div key={t.ticket_number} data-testid={`${testId}-row-${t.ticket_number}`}>
                <button
                  type="button"
                  onClick={() => { setOpenId(isOpen ? null : t.ticket_number); setResolutionNote(t.resolution_note || ''); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-zinc-800/40 transition-colors"
                  data-testid={`${testId}-toggle-${t.ticket_number}`}
                >
                  {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                          : <ChevronRight className="h-3.5 w-3.5 text-zinc-500 shrink-0" />}
                  <span className="flex-1 min-w-0 grid grid-cols-12 items-center gap-2 text-[11px]">
                    <span className="col-span-2 font-mono font-bold text-[#FFD60A]">{t.ticket_number}</span>
                    <span className="col-span-2 truncate text-white">{t.property_name || '—'}</span>
                    <span className="col-span-2 font-mono text-zinc-300">{t.service_event_number || '—'}</span>
                    <span className="col-span-2 truncate text-zinc-300">{t.reason?.slice(0, 40) || '—'}</span>
                    <span className="col-span-1 text-[9px] uppercase tracking-widest text-zinc-500">{reporterLabel}</span>
                    <span className="col-span-1 text-zinc-400">{ageOf(t.created_at)}</span>
                    <span className="col-span-1 truncate text-zinc-400">{t.assignee_id?.slice(0, 8) || '—'}</span>
                    <span className="col-span-1">
                      <Badge className={`${STATUS_COLORS[t.status] || 'bg-zinc-700 text-white'} text-[9px]`}>
                        {(t.status || 'open').toUpperCase()}
                      </Badge>
                    </span>
                  </span>
                </button>

                {isOpen && (
                  <div className="px-3 pb-3 pl-6 space-y-2 bg-zinc-950/40 text-[11px]" data-testid={`${testId}-detail-${t.ticket_number}`}>
                    <p className="text-zinc-400"><span className="text-zinc-500">Report:</span> {t.reason}</p>
                    {t.unit_label && <p className="text-zinc-400"><span className="text-zinc-500">Unit:</span> {t.unit_label}</p>}
                    {t.photo_url && (
                      <a href={t.photo_url} target="_blank" rel="noreferrer" className="inline-block">
                        <img src={t.photo_url} alt="ticket evidence" className="max-h-32 rounded border border-zinc-700" />
                      </a>
                    )}
                    <div>
                      <label className="text-[9px] uppercase tracking-widest text-zinc-500">Resolution note</label>
                      <Textarea rows={2} value={resolutionNote}
                                onChange={(e) => setResolutionNote(e.target.value)}
                                placeholder="Describe how this was resolved…"
                                className="bg-zinc-900 border-zinc-700 text-white text-xs"
                                data-testid={`${testId}-resolution-${t.ticket_number}`} />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {t.status === 'open' && (
                        <Button size="sm" disabled={busy}
                                onClick={() => transition(t, 'assigned')}
                                className="bg-amber-500 text-black hover:bg-amber-400 h-7 text-[10px] uppercase tracking-widest"
                                data-testid={`${testId}-assign-${t.ticket_number}`}>
                          Assign to me
                        </Button>
                      )}
                      {(t.status === 'open' || t.status === 'assigned') && (
                        <Button size="sm" disabled={busy || !resolutionNote.trim()}
                                onClick={() => transition(t, 'resolved')}
                                className="bg-blue-600 text-white hover:bg-blue-500 h-7 text-[10px] uppercase tracking-widest"
                                data-testid={`${testId}-resolve-${t.ticket_number}`}>
                          Resolve
                        </Button>
                      )}
                      <Button size="sm" disabled={busy || t.status !== 'resolved'}
                              onClick={() => transition(t, 'closed')}
                              className="bg-emerald-700 text-white hover:bg-emerald-600 h-7 text-[10px] uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid={`${testId}-close-${t.ticket_number}`}
                              title={t.status !== 'resolved' ? 'Ticket must be Resolved first' : ''}>
                        Close
                      </Button>
                    </div>
                    {(t.lifecycle || []).length > 0 && (
                      <div className="pt-2 border-t border-zinc-800 text-[10px] text-zinc-500">
                        <p className="text-zinc-400 mb-1">Lifecycle:</p>
                        <ol className="pl-3 list-decimal space-y-0.5">
                          {t.lifecycle.map((step, i) => (
                            <li key={i}>
                              <span className="text-zinc-300">{step.status}</span>
                              {step.at ? ` — ${new Date(step.at).toLocaleString()}` : ''}
                              {step.by ? ` · by ${step.by.slice(0, 8)}` : ''}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}


function KpiTile({ testId, label, value, accent }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/70 px-3 py-2" data-testid={testId}>
      <p className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className={`text-lg font-black leading-tight ${accent || 'text-white'}`} data-testid={`${testId}-value`}>
        {value}
      </p>
    </div>
  );
}
